import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { AlignLeft } from "lucide-react";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";

export const Toc = ({ sections }) => {
  const [currentSectionId, setCurrentSectionId] = useState(sections?.[0]?.id);
  const scrollHandlerLocked = useRef(false);
  sections = sections.filter((section) => [2, 3].includes(section.depth));

  const makeHeadings = useCallback(
    (sections) =>
      sections
        .map((section) => section.id)
        .map((id) => {
          const el = document.getElementById(id);
          if (!el) {
            return null;
          }

          const style = window.getComputedStyle(el);
          const scrollMt = parseFloat(style.scrollMarginTop);
          const top =
            window.scrollY + el.getBoundingClientRect().top - scrollMt;
          return { id, top };
        })
        .filter((h) => h !== null),
    [],
  );

  useEffect(() => {
    if (sections.length === 0) {
      return;
    }
    const headings = makeHeadings(sections);
    if (headings.length === 0) {
      return;
    }
    const handleScroll = () => {
      if (scrollHandlerLocked.current) {
        return;
      }

      const top = window.scrollY;
      let current = headings[0].id;

      for (const heading of headings) {
        if (top >= heading.top - 100) {
          current = heading.id;
        } else {
          break;
        }
      }
      setCurrentSectionId(current);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [sections, makeHeadings]);

  const isActive = (section) => section.id === currentSectionId || false;

  const setActive = (e, sectionId) => {
    if (e) {
      e.preventDefault();
    }
    scrollHandlerLocked.current = true;
    setCurrentSectionId(sectionId);
    const el = document.getElementById(sectionId);
    if (el) {
      const y = window.scrollY + el.getBoundingClientRect().top - 100;
      window.scroll({
        top: y,
        behavior: "smooth",
      });
    }
    setTimeout(() => (window.location.hash = sectionId), 200);
    // Update the URL hash
  };

  useEffect(() => {
    if (scrollHandlerLocked.current) {
      const timeout = setTimeout(
        () => (scrollHandlerLocked.current = false),
        800,
      );
      return () => clearTimeout(timeout);
    }
  }, [currentSectionId]);

  useEffect(() => {
    if (window.location.hash) {
      setActive(null, window.location.hash.replace("#", ""));
    }
  }, []);

  if (sections.length === 0) {
    return null;
  }

  return (
    <nav className="max-h-[58vh] overflow-y-auto pr-4 pb-8">
      <h3 className="-ml-0.5 flex items-center gap-1.5 text-sm text-gray-500">
        <AlignLeft className="h-4 w-4" />
        On this page
      </h3>

      <ul className="mt-4 grid gap-4 border-l-2 border-gray-200">
        {sections.map((section) => (
          <Item
            key={section.id}
            section={section}
            active={isActive(section)}
            onClick={(e) => setActive(e, section.id)}
          />
        ))}
      </ul>
    </nav>
  );
};
const Item = ({ section, active, onClick }) => {
  return (
    <li>
      <Link
        className={cn(
          section.depth < 3 ? "pl-0" : "pl-3",
          active ? "text-black" : "text-gray-500",
          "relative -ml-0.5 text-sm text-wrap transition-colors",
        )}
        style={{ paddingLeft: "16px" }}
        href={`#${section.id}`}
        onClick={onClick}
      >
        {section.title}
        {active && (
          <AnimatePresence>
            <motion.div
              layoutId="active-indicator"
              className="absolute top-0 left-0 h-full w-0.5 origin-top bg-black"
              initial={{ opacity: 0, translateY: -10 }}
              animate={{ opacity: 1, translateY: 0 }}
              exit={{ opacity: 0, translateY: 10 }}
              transition={{ duration: 0.1 }}
            />
          </AnimatePresence>
        )}
      </Link>
    </li>
  );
};
